import React, { useEffect, useState } from "react";
import "../CSS/contribution.css";
import Sidebar from "../Components/Sidebar";
import Swal from "sweetalert2";
import { BASE_API_URL, SERVER_API_URL } from "../constants";
import { useNavigate, useLocation } from "react-router-dom";

// --- Images Imports ---
import GPay from "../images/GPay.jpg";
import PhonePeLogo from "../images/phonepe.jpg"; // Renamed to avoid variable conflict
import Bhim from "../images/bhim.jpg";
import PaytmCircle from "../images/PaytmCircle.jpg";

const ContributionPage = () => {
  const [message, setMessage] = useState({ text: "", type: "" });
  const [projects, setProjects] = useState([]);
  const [paymentConfig, setPaymentConfig] = useState({
    minAmount: 5000,
    maxAmount: 50000,
  });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    projectId: "",
    amount: "",
    username: "",
    userId: "",
  });
  const [loading, setLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [totalContributions, setTotalContributions] = useState(0);
  const [redeemedAmount, setRedeemedAmount] = useState(0);
  const [selectedProjectName, setSelectedProjectName] = useState("");
  const [showProjectModal, setShowProjectModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const tiers = [
    {
      name: "Spotlight Contributor",
      icon: "",
      min: 5000,
      max: 9999,
      profit: "10-12%",
      benefits: [
        "Personalized thank-you mail",
        "Behind-the-scenes access",
        "Name on Supporter Wall",
      ],
      credit: "Website",
    },
    {
      name: "Silver Reel",
      icon: "",
      min: 10000,
      max: 19999,
      profit: "13-15%",
      benefits: [
        "All previous perks",
        "Contributor updates",
        "Digital certificate",
      ],
      credit: "Website (Priority)",
    },
    {
      name: "Golden Frame",
      icon: "",
      min: 20000,
      max: 29999,
      profit: "16-18%",
      benefits: [
        "All previous perks",
        "Online meet with cast/crew",
        "Early premiere link",
      ],
      credit: "Website + Optional Movie Credit",
    },
    {
      name: "Platinum Take",
      icon: "",
      min: 30000,
      max: 39999,
      profit: "19-22%",
      benefits: [
        "All previous perks",
        "Special Thanks credit",
        "Signed digital/physical poster",
      ],
      credit: "Website + Movie",
    },
    {
      name: "Producers Circle",
      icon: "",
      min: 40000,
      max: 50000,
      profit: "23-25%",
      benefits: [
        "All previous perks",
        "One-day film set visit",
        "Director-signed certificate",
      ],
      credit: "Website + Movie",
    },
  ];

  // -------------------- INIT LOAD --------------------
  useEffect(() => {
    const userId = sessionStorage.getItem("userId") || "";
    const username = sessionStorage.getItem("userName") || "";
    const email = sessionStorage.getItem("email") || "";
    const mobile = sessionStorage.getItem("phone") || sessionStorage.getItem("mobilenumber") || ""; //  Login stores "userName"

    // Get projectId from location state if passed from DiscoverPage or ProjectPage
    const selectedProjectId = location.state?.selectedProjectId || "";

    console.log(" Session Data:", { userId, username, selectedProjectId });

    setFormData((prev) => ({
      ...prev,
      userId,
      username,
      name: username,
      email,
      mobile,  //  Use userName from session directly
      projectId: selectedProjectId
    }));

    if (selectedProjectId) {
      // Immediately fetch the project name by ID
      console.log(" Fetching project name for ID:", selectedProjectId);
      fetchProjectNameById(selectedProjectId);
    }

    if (userId) {
      fetchUserSummary(userId);
      fetchUserProfile(userId);
    }

    loadPaymentConfig();
    loadProjects();
  }, [location.state?.selectedProjectId]);

  // After projects are loaded, find and set the project name
  useEffect(() => {
    // Get the projectId from location state (more reliable than formData due to state update timing)
    const selectedProjectId = location.state?.selectedProjectId || formData.projectId || "";

    console.log(" Finding project:", {
      selectedProjectId,
      projectsCount: projects.length,
      currentSelectedName: selectedProjectName
    });

    if (selectedProjectId) {
      // First, try to find in the loaded projects list
      if (projects.length > 0) {
        const selectedProject = projects.find(
          (p) => String(p.ProjectID) === String(selectedProjectId)
        );

        if (selectedProject) {
          setSelectedProjectName(selectedProject.title || "");
          console.log(" Project found in list:", selectedProject.title);
          return;
        }
      }

      // If not found in list, fetch directly by ID
      if (!selectedProjectName) {
        console.log(" Fetching project by ID...");
        fetchProjectNameById(selectedProjectId);
      }
    }
  }, [projects, location.state?.selectedProjectId, formData.projectId, selectedProjectName]);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  // -------------------- HANDLE PROJECT SELECTION --------------------
  const handleSelectProject = (projectId) => {
    const selectedProject = projects.find(
      (p) => String(p.ProjectID) === String(projectId)
    );

    if (selectedProject) {
      setFormData((prev) => ({ ...prev, projectId }));
      setSelectedProjectName(selectedProject.title || "");
      setShowProjectModal(false);
    }
  };

  const handleChangeProject = () => {
    setShowProjectModal(true);
  };

  // -------------------- LOAD CONFIGS --------------------
  const loadPaymentConfig = async () => {
    try {
      const response = await fetch(
        `${BASE_API_URL}/api/investor/contribution/config`
      );
      if (response.ok) setPaymentConfig(await response.json());
    } catch (err) {
      console.error(err);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await fetch(`${BASE_API_URL}/api/projects/approved`);
      console.log(" Projects API Status:", response.status);

      if (response.ok) {
        const data = await response.json();
        console.log(" Projects loaded:", data.length, "projects", data);

        // Ensure data is an array, not an error object
        if (Array.isArray(data)) {
          setProjects(data);
        } else {
          console.error(" Invalid projects format:", data);
          setProjects([]);
        }
      } else {
        console.error(" Failed to fetch projects:", response.status);
        setProjects([]);
      }
    } catch (err) {
      console.error(" Error loading projects:", err);
      setProjects([]);
    }
  };

  // Fetch single project by ID
  const fetchProjectNameById = async (projectId) => {
    try {
      const response = await fetch(`${BASE_API_URL}/api/projects/${projectId}`);
      if (response.ok) {
        const project = await response.json();
        console.log(" Project fetched by ID:", project.title);
        setSelectedProjectName(project.title || "");
      }
    } catch (err) {
      console.error("Error fetching project by ID:", err);
    }
  };

  const fetchUserSummary = async (userId) => {
    try {
      const res = await fetch(
        `${BASE_API_URL}/api/contributions/summary?userId=${userId}`
      );
      if (res.ok) {
        const data = await res.json();
        setWalletBalance(data.walletBalance || 0);
        setTotalContributions(data.totalContributions || 0);
      }
    } catch (err) {
      console.error("Error fetching user summary:", err);
    }
  };

  // Fetch user profile to get full name
  const fetchUserProfile = async () => {
    try {
      const email = sessionStorage.getItem("email") || "";

      if (!email) {
        console.log("No email found in session");
        return;
      }

      const res = await fetch(
        `${BASE_API_URL}/api/user-profile?email=${encodeURIComponent(email)}`
      );

      if (!res.ok) {
        console.log("Profile API failed:", res.status);
        return;
      }

      const data = await res.json();

      const user = data.user || {};

      console.log("Profile Data:", user);

      setFormData((prev) => ({
        ...prev,
        name:
          `${user.firstname || ""} ${user.lastname || ""}`.trim() ||
          prev.name,
        email: user.email || prev.email,
        mobile: String(user.phone || user.mobilenumber || ""),
      }));
    } catch (err) {
      console.error("Profile fetch error:", err);
    }
  };

  // -------------------- INPUT HANDLING --------------------
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    if (id === "amount" && redeemedAmount > 0) return;

    setFormData((prev) => ({ ...prev, [id]: value }));

    if (id === "amount") {
      const numericValue = parseInt(value, 10);
      const foundTier = tiers.find(
        (tier) => numericValue >= tier.min && numericValue <= tier.max
      );
      setSelectedTier(foundTier || null);
    }
  };

  const validateForm = () => {
    const emailRegex =
      /^[a-zA-Z0-9._%+-]+@([a-zA-Z]+(?:\.[a-zA-Z]{2,})+)$/;
    if (!formData.name.trim())
      return Swal.fire("Missing Field", "Please enter your name!", "warning");
    if (!emailRegex.test(formData.email))
      return Swal.fire(
        "Invalid Email",
        "Please enter a valid email!",
        "error"
      );
    if (!formData.mobile.match(/^\d{10}$/))
      return Swal.fire(
        "Invalid Mobile",
        "Mobile number must be 10 digits!",
        "error"
      );
    if (!formData.projectId)
      return Swal.fire(
        "Missing Selection",
        "Please select a project!",
        "warning"
      );
    if (
      !formData.amount ||
      formData.amount < paymentConfig.minAmount ||
      formData.amount > paymentConfig.maxAmount
    )
      return Swal.fire(
        "Invalid Amount",
        `Amount must be between ${paymentConfig.minAmount} and ${paymentConfig.maxAmount}.`,
        "error"
      );
    return true;
  };

  // -------------------- HANDLE SUBMIT --------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
      // Step 1: Get Auth + Prepare Payload
      const authRes = await fetch(`${SERVER_API_URL}/api/smepay_auth`);
      const authData = await authRes.json().catch(() => ({}));
      if (!authRes.ok) {
        throw new Error(authData.detail || "Failed to get access token");
      }
      const { access_token } = authData;

      const contributionAmount = parseInt(formData.amount || 0, 10);
      const referralCode = sessionStorage.getItem("referralCode") || new URLSearchParams(window.location.search).get("ref");
      
      const payload = {
        ...formData,
        amount: contributionAmount,
        redeemedAmount: redeemedAmount,
        referralCode: referralCode || null,
      };

      // Step 2: Call Order API
      Swal.fire({
        title: "Processing Payment...",
        html: `<div style='font-size:14px;color:#777;'>Creating payment link. Please wait...</div>`,
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const orderRes = await fetch(`${SERVER_API_URL}/api/smepay_order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${access_token}`,
        },
        body: JSON.stringify(payload),
      });

      const orderData = await orderRes.json();
      Swal.close();

      if (!orderRes.ok) {
        Swal.fire(
          "Error",
          orderData.detail || "Failed to process contribution.",
          "error"
        );
        setLoading(false);
        return;
      }

      // Step 3: Show QR Popup with Intents
      if (orderData.qr_code) {
        showQRPopup(
          orderData.qr_code,
          orderData.order_slug,
          orderData.ref_id,
          orderData.amount,
          orderData.order_id,
          orderData.transaction_id,
          orderData.intents //  Passing the intents here
        );
      } else {
        Swal.fire(
          "Info",
          "No QR code found. Please use the payment link instead.",
          "info"
        );
      }

      // Reset wallet redeem immediately
      // setRedeemedAmount(0);
    } catch (err) {
      console.error(err);
      Swal.close();
      Swal.fire(
        "Error",
        err?.message || "Network error occurred. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // -------------------- COMMON HANDLER FOR PAYMENT RESULT --------------------
  const handlePaymentResult = async (
    paymentStatus,
    amount,
    slug,
    order_id,
    transaction_id
  ) => {
    try {
      const normalizedStatus = (paymentStatus || "").toUpperCase();

      // Step 1: Call Payment Callback API (for record keeping)
      const callbackBody = {
        order_id,
        transaction_id,
        amount,
        payment_status: normalizedStatus,
        redeemed_amount: redeemedAmount,
      };

      try {
        await fetch(`${BASE_API_URL}/api/payment/callback`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(callbackBody),
        });
        setRedeemedAmount(0);
      } catch (err) {
        console.error("Payment callback error:", err);
      }

      if (normalizedStatus === "SUCCESS") {
        //  2 Validate payment
        await validatePayment(amount, slug);

        //  3 Assign badge via your /api/badges/assign API
        const badgePayload = {
          userId: Number(formData.userId),
          projectId: Number(formData.projectId),
          tierName: selectedTier?.name || "Contributor",
          tierIcon: selectedTier?.icon || "",
          profit: selectedTier?.profit || "",
          amount: Number(amount),
        };

        try {
          const badgeRes = await fetch(
            `${BASE_API_URL}/api/badges/assign`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(badgePayload),
            }
          );

          const badgeData = await badgeRes.json().catch(() => ({}));

          if (!badgeRes.ok) {
            Swal.fire(
              " Badge Assign Failed",
              badgeData.detail || "Could not assign contribution badge.",
              "warning"
            );
          } else {
            Swal.fire(
              " Payment Successful",
              "Your contribution has been received and badge assigned!",
              "success"
            );
          }
        } catch (err) {
          console.error("Badge assign error:", err);
          Swal.fire(
            " Payment Successful",
            "Payment succeeded, but badge could not be assigned automatically. Please contact support if needed.",
            "warning"
          );
        }

        //  4 Update wallet / summary
        fetchUserSummary(formData.userId);

        //  5 Redirect to contracts / dashboard
        navigate("/contracts");
      } else if (normalizedStatus === "FAILED") {
        Swal.fire(
          "Payment Failed",
          "Payment was not completed or failed.",
          "error"
        );
      } else {
        // PENDING / UNKNOWN
        Swal.fire(
          "Pending / Not Confirmed",
          "We could not confirm the payment yet. If the amount is debited, it may get updated shortly in your contribution history.",
          "info"
        );
      }
    } catch (err) {
      console.error("handlePaymentResult error:", err);
      Swal.fire(
        "Notice",
        "There was an issue while processing final status. Please check your contribution history after some time.",
        "warning"
      );
    }
  };

  // -------------------- POPUP WITH INTENTS UI + POLLING --------------------
  const showQRPopup = (
    qrBase64,
    slug,
    ref_id,
    amount,
    order_id,
    transaction_id,
    intents
  ) => {
    // Prepare Icons HTML
    const logoMap = {
      gpay: GPay,
      paytm: PaytmCircle,
      phonepe: PhonePeLogo,
      bhim: Bhim,
    };

    let appsHtml = "";
    if (intents) {
      appsHtml = `<div style="display: flex; justify-content: center; gap: 15px; margin-top: 15px; padding-top:10px; border-top: 1px dashed #eee; flex-wrap: wrap;">`;

      Object.keys(intents).forEach((key) => {
        const url = intents[key];
        const logoSrc = logoMap[key];

        if (url && logoSrc) {
          appsHtml += `
            <a href="${url}" target="_blank" style="text-decoration: none; display: flex; flex-direction: column; align-items: center; cursor: pointer;">
              <div style="width: 50px; height: 50px; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px rgba(0,0,0,0.1); border: 1px solid #f0f0f0; transition: transform 0.2s;" 
                   onmouseover="this.style.transform='scale(1.1)'" 
                   onmouseout="this.style.transform='scale(1)'">
                <img src="${logoSrc}" alt="${key}" style="width: 100%; height: 100%; object-fit: cover;" />
              </div>
              <span style="font-size: 11px; color: #555; margin-top: 4px; text-transform: capitalize; font-weight: 600;">${key === "gpay" ? "GPay" : key === "bhim" ? "BHIM" : key
            }</span>
            </a>
          `;
        }
      });
      appsHtml += `</div>`;
    }

    let countdownInterval = null;
    let pollInterval = null;
    let timeoutHandle = null;
    let finished = false; // prevent double execution

    const stopAllTimers = () => {
      if (countdownInterval) clearInterval(countdownInterval);
      if (pollInterval) clearInterval(pollInterval);
      if (timeoutHandle) clearTimeout(timeoutHandle);
    };

    Swal.fire({
      title: "",
      html: `
      <div style="font-family: 'Segoe UI', Arial, sans-serif; text-align:center; padding:10px; max-width:400px;">
        <h3 style="margin: 0 0 5px 0; color: #333;">Vithal Visions Private Limited</h3>
        <div style="font-size:12px; color:#27ae60; margin-bottom:15px; display:flex; align-items:center; justify-content:center; gap:5px;">
           <span></span> Verified Merchant
        </div>
        
        <div style="background: #ffffff; border: 1px solid #e0e0e0; border-radius: 16px; padding: 20px; box-shadow: 0 10px 25px rgba(0,0,0,0.05);">
          <div style="font-size:14px; color:#777; margin-bottom:5px;">Total Payable</div>
          <div style="font-size:28px; font-weight:800; color:#333; margin-bottom:15px;">${amount}</div>
          
          <div style="position: relative; width: 220px; height: 220px; margin: 0 auto;">
            <img src="data:image/png;base64,${qrBase64}" 
                 style="width: 100%; height: 100%; border-radius: 12px; border: 1px solid #eee;" />
          </div>
          <div style="font-size:13px; color:#888; margin-top:10px;">Scan with any UPI App</div>
        </div>

        <!-- Payment Apps Section -->
        ${appsHtml
          ? `<div style="margin-top: 20px;">
            <div style="font-size: 13px; color: #666; font-weight: 500; margin-bottom: 5px;">Or pay directly via</div>
            ${appsHtml}
        </div>`
          : ""
        }

        <div id="payment-timer" style="margin-top:20px; font-weight:bold; color:#e74c3c; background: #fff5f5; padding: 8px; border-radius: 8px; display: inline-block;">
             Time left: 2:00
        </div>
        <div style="margin-top:12px; font-size:11px; color:#999;">
             Please dont refresh the page before completing the transaction
        </div>
      </div>
    `,
      showConfirmButton: false, // no "I've paid"
      showCancelButton: false,  // no "Cancel"
      allowOutsideClick: false,
      width: 480,
      padding: "1em",
      didOpen: () => {
        //  2-minute countdown
        let remaining = 120;
        const timerElement = document.getElementById("payment-timer");

        countdownInterval = setInterval(() => {
          if (timerElement) {
            const minutes = Math.floor(remaining / 60);
            const seconds = (remaining % 60).toString().padStart(2, "0");
            timerElement.innerText = ` Time left: ${minutes}:${seconds}`;
          }
          remaining--;
          if (remaining < 0) {
            clearInterval(countdownInterval);
          }
        }, 1000);

        //  Poll /api/smepay_status every 5 seconds
        pollInterval = setInterval(async () => {
          if (finished) return;
          try {
            const statusRes = await fetch(
              `${SERVER_API_URL}/api/smepay_status`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ slug, ref_id }),
              }
            );

            if (!statusRes.ok) {
              console.error("smepay_status poll non-200:", statusRes.status);
              return;
            }

            let statusData;
            try {
              statusData = await statusRes.json();
            } catch (err) {
              console.error("smepay_status poll JSON error:", err);
              return;
            }

            if (statusData?.status && statusData?.payment_status) {
              const paymentStatus =
                statusData.payment_status.toUpperCase();

              if (paymentStatus === "SUCCESS") {
                finished = true;
                stopAllTimers();
                Swal.close(); // close QR popup
                await handlePaymentResult(
                  paymentStatus,
                  amount,
                  slug,
                  order_id,
                  transaction_id
                );
              } else if (paymentStatus === "FAILED") {
                finished = true;
                stopAllTimers();
                Swal.close();
                await handlePaymentResult(
                  paymentStatus,
                  amount,
                  slug,
                  order_id,
                  transaction_id
                );
              }
              // If PENDING  keep polling
            }
          } catch (err) {
            console.error("Polling smepay_status error:", err);
          }
        }, 5000);

        //  Final fallback after 2 minutes
        timeoutHandle = setTimeout(async () => {
          if (finished) return;
          finished = true;
          stopAllTimers();
          Swal.close(); // close QR popup

          // One last status check (but no "could not verify" error anymore)
          try {
            const finalRes = await fetch(
              `${SERVER_API_URL}/api/smepay_status`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ slug, ref_id }),
              }
            );

            if (!finalRes.ok) {
              console.error(
                "Final smepay_status non-200:",
                finalRes.status
              );
              await handlePaymentResult(
                "PENDING",
                amount,
                slug,
                order_id,
                transaction_id
              );
              return;
            }

            let finalData;
            try {
              finalData = await finalRes.json();
            } catch (err) {
              console.error("Final smepay_status JSON error:", err);
              await handlePaymentResult(
                "PENDING",
                amount,
                slug,
                order_id,
                transaction_id
              );
              return;
            }

            const finalStatus =
              finalData?.payment_status?.toUpperCase() || "PENDING";

            await handlePaymentResult(
              finalStatus,
              amount,
              slug,
              order_id,
              transaction_id
            );
          } catch (err) {
            console.error("Final status check error:", err);
            await handlePaymentResult(
              "PENDING",
              amount,
              slug,
              order_id,
              transaction_id
            );
          }
        }, 120000);
      },
      willClose: () => {
        // Cleanup if closed for any reason
        stopAllTimers();
      },
    });
  };

  // -------------------- VALIDATE PAYMENT --------------------
  const validatePayment = async (amount, slug) => {
    try {
      const validateRes = await fetch(`${SERVER_API_URL}/api/smepay_validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount, slug }),
      });

      const validateData = await validateRes.json();

      if (validateData.status)
        setMessage({
          text: "Payment validated successfully!",
          type: "success",
        });
      else
        setMessage({
          text: "Validation failed. Contact support.",
          type: "error",
        });
    } catch (err) {
      console.error("Validate Payment Error:", err);
    }
  };

  // -------------------- WALLET REDEEM --------------------
  const handleRedeem = async () => {
    if (totalContributions <= 0) {
      Swal.fire(
        "Not Eligible",
        "You must complete at least one successful contribution before redeeming your wallet.",
        "warning"
      );
      return;
    }

    if (walletBalance <= 0) {
      Swal.fire("No Balance", "Your wallet balance is zero.", "info");
      return;
    }

    try {
      const res = await fetch(`${BASE_API_URL}/api/contributor/redeem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: formData.userId }),
      });

      if (!res.ok) throw new Error("Redeem failed");

      const data = await res.json();
      const redeemed = data?.redeemedAmount || walletBalance;

      const newAmount = Math.max(
        parseInt(formData.amount || 0, 10) - redeemed,
        0
      );

      setFormData((prev) => ({ ...prev, amount: newAmount }));
      setWalletBalance((prev) => prev - redeemed);
      setRedeemedAmount(redeemed);

      Swal.fire(
        "Redeem Successful ",
        data.message || `Wallet redeemed ${redeemed} successfully!`,
        "success"
      );
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Redeem failed. Try again later.", "error");
    }
  };

  // -------------------- UI --------------------
  return (
    <>
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <main className="cb-main-content">
        <div className="section-header">
          <h2 className="section-title">Contribute to a Film Project</h2>
        </div>

        <div className="wallet-box">
          <h3>
            Wallet Balance: {walletBalance.toLocaleString("en-IN")}
          </h3>
          <button className="redeem-btn" onClick={handleRedeem}>
            Redeem Wallet
          </button>
        </div>

        <form className="contribution-form" onSubmit={handleSubmit}>
          <input type="hidden" id="username" value={formData.username} />
          <input type="hidden" id="userId" value={formData.userId} />
          <div className="cb-form-group">
            <label htmlFor="userId">User ID</label>
            <input
              type="text"
              id="userId"
              className="form-control"
              value={formData.userId}
              readOnly
            />
          </div>
          <div className="cb-form-group">
            <label htmlFor="name">Your Name</label>
            <input
              type="text"
              id="name"
              className="form-control"
              value={formData.name}
              readOnly
            />
          </div>
          <div className="cb-form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              className="form-control"
              value={formData.email}
              readOnly
            />
          </div>
          <div className="cb-form-group">
            <label htmlFor="mobile">Mobile Number</label>
            <input
              type="tel"
              id="mobile"
              className="form-control"
              value={formData.mobile}
              readOnly
            />
          </div>
          <div className="cb-form-group">
            <label htmlFor="projectId">Project Selection</label>
            {formData.projectId ? (
              <div className="project-selected-container">
                <input
                  type="text"
                  className="form-control project-field"
                  value={selectedProjectName}
                  placeholder="Loading..."
                  readOnly
                  style={{
                    backgroundColor: "#f0f8ff",
                    cursor: "not-allowed",
                    fontWeight: "600",
                    color: "#1e3a5f"
                  }}
                />
              </div>
            ) : (
              <button
                type="button"
                className="select-project-btn"
                onClick={handleChangeProject}
                style={{
                  width: "100%",
                  padding: "0.8rem 1rem",
                  backgroundColor: "#FF3CAC",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "1rem",
                  fontWeight: "600",
                  transition: "all 0.3s ease"
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#E63B9B";
                  e.target.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#FF3CAC";
                  e.target.style.transform = "translateY(0)";
                }}
              >
                Select a Project
              </button>
            )}
          </div>

          {/* Project Selection Modal */}
          {showProjectModal && (
            <div style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000
            }}>
              <div style={{
                backgroundColor: "white",
                borderRadius: "8px",
                padding: "2rem",
                maxWidth: "500px",
                maxHeight: "70vh",
                overflow: "auto",
                boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)"
              }}>
                <h2 style={{ marginBottom: "1.5rem", color: "#333" }}>Select a Project</h2>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.8rem" }}>
                  {projects.length > 0 ? (
                    projects.map((project) => (
                      <button
                        key={project.ProjectID}
                        onClick={() => handleSelectProject(project.ProjectID)}
                        style={{
                          padding: "1rem",
                          border: "2px solid #FF3CAC",
                          backgroundColor: "#f5f5f5",
                          borderRadius: "5px",
                          cursor: "pointer",
                          textAlign: "left",
                          transition: "all 0.3s ease",
                          fontWeight: "600",
                          color: "#333"
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = "#FF3CAC";
                          e.target.style.color = "white";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = "#f5f5f5";
                          e.target.style.color = "#333";
                        }}
                      >
                        {project.title}
                      </button>
                    ))
                  ) : (
                    <p style={{ color: "#999" }}>Loading projects...</p>
                  )}
                </div>
                <button
                  onClick={() => setShowProjectModal(false)}
                  style={{
                    marginTop: "1.5rem",
                    width: "100%",
                    padding: "0.8rem",
                    backgroundColor: "#6c757d",
                    color: "white",
                    border: "none",
                    borderRadius: "5px",
                    cursor: "pointer",
                    fontWeight: "600"
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          )}
          <div className="cb-form-group">
            <label htmlFor="amount">Contribution Amount ()</label>
            <input
              type="number"
              id="amount"
              className="form-control"
              value={formData.amount}
              onChange={handleInputChange}
              min={paymentConfig.minAmount}
              max={paymentConfig.maxAmount}
              placeholder="Enter amount"
              required
            />
            <div className="amount-range">
              Valid range: {paymentConfig.minAmount} -
              {paymentConfig.maxAmount}
            </div>
          </div>

          {redeemedAmount > 0 && (
            <div className="cb-form-group">
              <label htmlFor="redeemed">Wallet Redeemed ()</label>
              <input
                type="number"
                id="redeemed"
                className="form-control"
                value={redeemedAmount}
                readOnly
              />
            </div>
          )}

          {selectedTier && (
            <div className="tier-display">
              <h3>
                {selectedTier.icon} {selectedTier.name}
              </h3>
              <p className="tier-profit">
                Profit: {selectedTier.profit}
              </p>
              <ul>
                {selectedTier.benefits.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
              <p className="tier-credit">
                Credit: {selectedTier.credit}
              </p>
            </div>
          )}

          <button
            type="submit"
            className="submit-btn"
            disabled={loading}
          >
            {loading
              ? "Processing please wait..."
              : "Process Contribution"}
          </button>

          {loading && (
            <div className="loading">
              <div className="spinner"></div>
              <div>Processing your contribution...</div>
            </div>
          )}

          {message.text && (
            <div className={`message ${message.type}`}>
              {message.text}
            </div>
          )}
        </form>
      </main>
    </>
  );
};

export default ContributionPage;


